import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, CopyButton, Input } from '../components/ui';
import { usePageTitle } from '../lib/title';

const sample = 'VOS Bridge 클립보드 테스트 · 한글 English 123 🎉';
const cursors = [
  'default',
  'pointer',
  'text',
  'crosshair',
  'move',
  'not-allowed',
  'wait',
  'grab',
  'col-resize',
  'zoom-in',
] as const;

/** Public test content only: no account, node credentials, or privileged test endpoints. */
export function TestPage() {
  usePageTitle('브라우저 기능 테스트');
  const [logs, setLogs] = useState<string[]>([]);
  const [pasted, setPasted] = useState('');
  const record = (message: string) =>
    setLogs((old) => [`${new Date().toLocaleTimeString()}  ${message}`, ...old].slice(0, 20));
  return (
    <main className="min-h-screen bg-canvas text-ink">
      <header className="bg-slate-950 px-6 py-12 text-white">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs tracking-widest text-sky-300">VOS BRIDGE / TEST LAB</p>
          <h1 className="mt-3 text-3xl font-semibold">브라우저 기능을 하나씩 확인해보세요.</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">
            로그인 없이 사용할 수 있는 테스트 페이지입니다. 원격 기능을 확인하려면 운영자 뷰어의
            주소창에서 이 페이지를 열어주세요. 일반 브라우저에서 직접 열면 해당 브라우저의 기본
            동작을 확인할 수 있습니다.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <code className="break-all text-sm">https://bridge.proswin.kr/test</code>
            <CopyButton text="https://bridge.proswin.kr/test" />
          </div>
        </div>
      </header>
      <div className="mx-auto grid max-w-5xl gap-6 px-6 py-8 md:grid-cols-2">
        <Card title="01 · 알림과 응답">
          <p className="mb-4 text-sm text-ink-muted">
            원격 뷰어에 대화상자가 나타나고, 응답하면 아래 결과가 바뀌어야 합니다. 관찰자는 볼 수
            있지만 응답은 제어자만 할 수 있습니다.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                window.alert('원격 alert 테스트입니다. 확인을 눌러주세요.');
                record('alert 확인 완료');
              }}
            >
              alert 열기
            </Button>
            <Button
              onClick={() =>
                record(
                  `confirm 결과: ${window.confirm('확인 또는 취소를 선택해주세요.') ? '확인' : '취소'}`,
                )
              }
            >
              confirm 열기
            </Button>
            <Button
              onClick={() => {
                const result = window.prompt('테스트 문구를 입력해주세요.', '안녕하세요');
                record(`prompt 결과: ${result === null ? '취소' : JSON.stringify(result)}`);
              }}
            >
              prompt 열기
            </Button>
            <Button
              onClick={() => {
                window.alert('첫 번째 알림');
                window.alert('두 번째 알림');
                record('연속 알림 2개 확인 완료');
              }}
            >
              연속 알림
            </Button>
          </div>
        </Card>
        <Card title="02 · 양방향 클립보드">
          <p className="mb-3 text-sm text-ink-muted">
            문구를 선택한 뒤 Ctrl/Cmd+C 또는 뷰어의 ‘선택 내용 복사’를 누르고, 내 PC의 메모장에
            붙여넣어보세요. 반대로 내 PC에서 복사한 글을 아래 입력란에 붙여넣어보세요.
          </p>
          <Input
            aria-label="복사할 테스트 문구"
            defaultValue={sample}
            onFocus={(e) => e.target.select()}
          />
          <textarea
            aria-label="붙여넣기 테스트"
            placeholder="내 PC에서 복사한 내용을 여기에 붙여넣기"
            className="mt-3 min-h-24 w-full rounded border border-line bg-surface p-3 text-sm"
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
          />
          <p className="mt-2 text-xs text-ink-muted">
            입력된 글자 수: {pasted.length} · 텍스트만 지원하며 비밀번호 입력란의 선택 내용은
            복사하지 않습니다.
          </p>
          <p className="mt-3 select-text rounded bg-canvas p-3 text-sm">
            일반 본문에서도 이 문장을 드래그해 복사할 수 있습니다.
          </p>
        </Card>
        <Card title="03 · 원격 커서">
          <p className="mb-4 text-sm text-ink-muted">
            각 영역 위로 마우스를 움직이면 표시된 모양으로 커서가 바뀌어야 합니다.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {cursors.map((cursor) => (
              <div
                key={cursor}
                style={{ cursor }}
                className="rounded-lg border border-line bg-canvas px-3 py-5 text-center font-mono text-xs"
              >
                {cursor}
              </div>
            ))}
          </div>
        </Card>
        <Card title="04 · 페이지 이동과 실패">
          <p className="mb-4 text-sm text-ink-muted">
            아래 링크로 이동한 뒤 뷰어의 뒤로·앞으로·새로고침을 확인하세요. 실패 응답은 아래 주소를
            원격 뷰어 주소창에 넣어 확인합니다. 실패 시 오류 안내가 나타나야 합니다.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <a className="text-primary underline" href="/test?step=2">
              테스트 페이지 2로 이동
            </a>
            <a className="text-primary underline" href="/test">
              첫 페이지로 이동
            </a>
          </div>
          <p className="mt-3 text-sm">
            현재 단계: {new URLSearchParams(window.location.search).get('step') ?? '1'}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <code className="min-w-0 break-all text-xs">https://navigation-failure.invalid</code>
            <CopyButton text="https://navigation-failure.invalid" />
          </div>
        </Card>
        <Card title="응답 기록" className="md:col-span-2">
          <div
            role="log"
            aria-live="polite"
            className="min-h-24 space-y-2 rounded-lg bg-slate-950 p-4 font-mono text-sm text-sky-100"
          >
            {logs.length ? (
              logs.map((log, i) => <p key={i}>{log}</p>)
            ) : (
              <p className="text-slate-400">대화상자에 응답하면 결과가 여기에 표시됩니다.</p>
            )}
          </div>
          <Button className="mt-3" size="sm" onClick={() => setLogs([])}>
            기록 비우기
          </Button>
        </Card>
        <Card title="관리 화면에서 확인할 항목" className="md:col-span-2">
          <p className="mb-4 text-sm text-ink-muted">
            아래 항목은 실제 운영 상태를 바꾸므로 로그인한 관리 화면과 테스트용 프로필에서
            확인합니다. 이 공개 페이지에서는 관리 작업을 실행하지 않습니다.
          </p>
          <ol className="list-decimal space-y-3 pl-5 text-sm leading-6">
            <li>
              <strong>제어권 복구:</strong> 고객과 운영자가 접속한 상태에서 제어자를 정한 뒤 API를
              재시작하세요. 연결이 복구되면 같은 사용자에게 제어권이 돌아오는지, 다른 사용자가 먼저
              재접속해도 예약이 유지되는지 확인합니다.
            </li>
            <li>
              <strong>검색·페이지:</strong>{' '}
              <Link className="text-primary underline" to="/profiles">
                프로필 목록
              </Link>
              에서 프로필 이름·고객 이름·ID를 검색하고 이전·다음 페이지와 보관된 프로필 포함을
              확인합니다. 한 페이지는 50개입니다.
            </li>
            <li>
              <strong>업데이트 요청:</strong>{' '}
              <Link className="text-primary underline" to="/nodes">
                노드 목록
              </Link>
              에서 요청한 뒤 다운로드와 ‘프로필 종료 대기’ 상태를 확인합니다. 프로필을 모두 닫으면
              설치되고 노드가 다시 접속해야 합니다.
            </li>
            <li>
              <strong>강제 업데이트:</strong> 새 버전이 있을 때 테스트용 프로필을 열고 실행합니다.
              다운로드 후 프로필이 종료되고 설치·재접속되는지 확인합니다. 최신 버전이라면 프로필을
              종료하지 않아야 합니다.
            </li>
          </ol>
        </Card>
      </div>
    </main>
  );
}
